import { useMemo, useState } from "react"
import { Box, Button, HStack, Input, Stack, Table } from "@chakra-ui/react"
import { FaSortDown, FaSortUp, FaSort } from "react-icons/fa"
import { Text } from "@/components"
import { CommissionSummary } from "@/types"

const fmt$ = (v: number) => {
  if (v < 0) return `($${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

type SortKey = keyof CommissionSummary
type SortDir = "asc" | "desc"

const COLS: { key: SortKey; label: string; align: "left" | "right" }[] = [
  { key: "dmName", label: "Deal Manager", align: "left" },
  { key: "totalDeals", label: "Deals", align: "right" },
  { key: "exitedDeals", label: "Exited", align: "right" },
  { key: "liveDeals", label: "Live", align: "right" },
  { key: "totalInvested", label: "Invested", align: "right" },
  { key: "totalProfit", label: "Profit", align: "right" },
  { key: "moic", label: "MOIC", align: "right" },
  { key: "cumulativeCalculated", label: "Commission", align: "right" },
]

interface Props {
  summaries: CommissionSummary[]
  onSelectDm: (summary: CommissionSummary) => void
  selectedPersonId: number | null
}

export const CommissionsSummaryTable = ({ summaries, onSelectDm, selectedPersonId }: Props) => {
  const [search, setSearch] = useState("")
  const [sortKey, setSortKey] = useState<SortKey>("missingCommission")
  const [sortDir, setSortDir] = useState<SortDir>("desc")

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir("desc")
    }
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return summaries
      .filter((s) => !q || s.dmName.toLowerCase().includes(q))
      .sort((a, b) => {
        const av = a[sortKey]
        const bv = b[sortKey]
        if (av == null && bv == null) return 0
        if (av == null) return 1
        if (bv == null) return -1
        const compare = typeof av === "string"
          ? av.localeCompare(bv as string)
          : (av as number) - (bv as number)
        return sortDir === "asc" ? compare : -compare
      })
  }, [summaries, search, sortKey, sortDir])

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <FaSort style={{ opacity: 0.3 }} />
    return sortDir === "asc" ? <FaSortUp /> : <FaSortDown />
  }

  // Totals row
  const totals = useMemo(() => ({
    totalDeals: summaries.reduce((s, r) => s + r.totalDeals, 0),
    exitedDeals: summaries.reduce((s, r) => s + r.exitedDeals, 0),
    liveDeals: summaries.reduce((s, r) => s + r.liveDeals, 0),
    totalInvested: summaries.reduce((s, r) => s + r.totalInvested, 0),
    totalProfit: summaries.reduce((s, r) => s + r.totalProfit, 0),
    cumulativeCalculated: summaries.reduce((s, r) => s + r.cumulativeCalculated, 0),
  }), [summaries])

  return (
    <Stack gap={3}>
      <HStack>
        <Input
          placeholder="Search DM..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          maxW="240px"
          size="sm"
        />
        <Box ml="auto">
          <Text fontSize="sm" color="text.muted">
            {filtered.length} deal managers
          </Text>
        </Box>
      </HStack>

      <Box overflowX="auto" borderWidth="1px" borderColor="border.default" borderRadius="md">
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row bg="table.header">
              {COLS.map((col) => (
                <Table.ColumnHeader
                  key={col.key}
                  textAlign={col.align}
                  cursor="pointer"
                  userSelect="none"
                  onClick={() => handleSort(col.key)}
                  whiteSpace="nowrap"
                  px={3}
                  py={2}
                >
                  <HStack gap={1} justifyContent={col.align === "right" ? "flex-end" : "flex-start"}>
                    <Text fontSize="xs" fontWeight="semibold">{col.label}</Text>
                    <Box fontSize="xs"><SortIcon k={col.key} /></Box>
                  </HStack>
                </Table.ColumnHeader>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {/* Totals row */}
            <Table.Row bg="blue.50" fontWeight="bold">
              <Table.Cell px={3} py={2}><Text fontSize="xs">ALL DMs</Text></Table.Cell>
              <Table.Cell textAlign="right" px={3} py={2}><Text fontSize="xs">{totals.totalDeals}</Text></Table.Cell>
              <Table.Cell textAlign="right" px={3} py={2}><Text fontSize="xs">{totals.exitedDeals}</Text></Table.Cell>
              <Table.Cell textAlign="right" px={3} py={2}><Text fontSize="xs">{totals.liveDeals}</Text></Table.Cell>
              <Table.Cell textAlign="right" px={3} py={2}><Text fontSize="xs">{fmt$(totals.totalInvested)}</Text></Table.Cell>
              <Table.Cell textAlign="right" px={3} py={2}><Text fontSize="xs">{fmt$(totals.totalProfit)}</Text></Table.Cell>
              <Table.Cell textAlign="right" px={3} py={2}><Text fontSize="xs">—</Text></Table.Cell>
              <Table.Cell textAlign="right" px={3} py={2}><Text fontSize="xs">{fmt$(totals.cumulativeCalculated)}</Text></Table.Cell>
            </Table.Row>

            {filtered.map((s) => (
              <Table.Row
                key={s.personId}
                _hover={{ bg: "hover.bg", cursor: "pointer" }}
                bg={selectedPersonId === s.personId ? "blue.50" : undefined}
                onClick={() => onSelectDm(s)}
              >
                <Table.Cell px={3} py={2}>
                  <Text fontSize="sm" fontWeight={selectedPersonId === s.personId ? "semibold" : "normal"}>
                    {s.dmName}
                  </Text>
                </Table.Cell>
                <Table.Cell textAlign="right" px={3} py={2}><Text fontSize="sm">{s.totalDeals}</Text></Table.Cell>
                <Table.Cell textAlign="right" px={3} py={2}><Text fontSize="sm">{s.exitedDeals}</Text></Table.Cell>
                <Table.Cell textAlign="right" px={3} py={2}><Text fontSize="sm">{s.liveDeals}</Text></Table.Cell>
                <Table.Cell textAlign="right" px={3} py={2}><Text fontSize="sm">{fmt$(s.totalInvested)}</Text></Table.Cell>
                <Table.Cell textAlign="right" px={3} py={2}>
                  <Text fontSize="sm" color={s.totalProfit >= 0 ? undefined : "red.500"}>{fmt$(s.totalProfit)}</Text>
                </Table.Cell>
                <Table.Cell textAlign="right" px={3} py={2}>
                  <Text fontSize="sm">{s.moic != null ? `${s.moic.toFixed(2)}x` : "—"}</Text>
                </Table.Cell>
                <Table.Cell textAlign="right" px={3} py={2}><Text fontSize="sm">{fmt$(s.cumulativeCalculated)}</Text></Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>
    </Stack>
  )
}
